import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useROStore } from "../context/ROContext";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function polishName(value) {
  return safeString(value)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) =>
      word
        .split(/([-'])/)
        .map((piece) =>
          piece === "-" || piece === "'"
            ? piece
            : piece.charAt(0).toUpperCase() + piece.slice(1)
        )
        .join("")
    )
    .join(" ");
}

function splitDisplayName(user) {
  const first = safeString(user?.firstName).trim();
  const last = safeString(user?.lastName).trim();

  if (first || last) {
    return { firstName: first, lastName: last };
  }

  const full = safeString(
    user?.name || user?.fullName || user?.displayName || ""
  ).trim();

  if (!full) {
    return { firstName: "", lastName: "" };
  }

  const parts = full.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function prettyRole(role) {
  const r = safeString(role).trim().toLowerCase();
  if (!r) return "—";
  if (r === "service_manager" || r === "store_manager") return "Manager";
  if (r === "tech") return "Technician";

  return r
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function SettingsPage() {
  const auth = useAuth();
  const store = useROStore();

  const authUser = auth?.user || null;
  const storeUser =
    store?.currentUser && typeof store.currentUser === "object"
      ? store.currentUser
      : null;

  const baseUser = authUser || storeUser || null;

  const initialNameParts = useMemo(() => splitDisplayName(baseUser), [baseUser]);

  const [firstName, setFirstName] = useState(initialNameParts.firstName);
  const [lastName, setLastName] = useState(initialNameParts.lastName);
  const [email, setEmail] = useState(safeString(baseUser?.email).trim());

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    setFirstName(initialNameParts.firstName);
    setLastName(initialNameParts.lastName);
    setEmail(safeString(baseUser?.email).trim());
  }, [initialNameParts, baseUser?.email]);

  const polishedDisplayName = useMemo(() => {
    return [polishName(firstName), polishName(lastName)]
      .filter(Boolean)
      .join(" ")
      .trim();
  }, [firstName, lastName]);

  const roleText = useMemo(() => {
    const platformRole = safeString(
      auth?.platformRole || authUser?.platformRole
    ).trim();

    if (platformRole) {
      return platformRole
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (m) => m.toUpperCase());
    }

    const membershipRole = safeString(
      auth?.membership?.role || baseUser?.role
    ).trim();

    return prettyRole(membershipRole);
  }, [
    auth?.platformRole,
    auth?.membership?.role,
    authUser?.platformRole,
    baseUser?.role,
  ]);

  const storeName = useMemo(() => {
    return (
      safeString(auth?.membership?.storeName).trim() ||
      safeString(auth?.membership?.store_name).trim() ||
      safeString(store?.currentStore?.dealerName).trim() ||
      safeString(store?.currentStore?.dealer_name).trim() ||
      safeString(store?.currentStore?.name).trim() ||
      "—"
    );
  }, [auth?.membership, store?.currentStore]);

  async function handleSave(e) {
    e.preventDefault();

    const polishedFirst = polishName(firstName);
    const polishedLast = polishName(lastName);
    const normalizedEmail = safeString(email).trim().toLowerCase();

    setSaveMessage("");
    setSaveError("");

    if (!polishedFirst) {
      setSaveError("First name is required.");
      return;
    }

    if (!polishedLast) {
      setSaveError("Last name is required.");
      return;
    }

    if (!normalizedEmail) {
      setSaveError("Email is required.");
      return;
    }

    setSaving(true);

    try {
      const data =
        typeof auth?.updateProfile === "function"
          ? await auth.updateProfile({
              firstName: polishedFirst,
              lastName: polishedLast,
              email: normalizedEmail,
            })
          : typeof auth?.apiPatch === "function"
          ? await auth.apiPatch("/api/auth/me", {
              firstName: polishedFirst,
              lastName: polishedLast,
              email: normalizedEmail,
            })
          : await fetch(
              `${safeString(auth?.apiBase).replace(/\/+$/, "")}/api/auth/me`,
              {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${auth?.token || ""}`,
                },
                body: JSON.stringify({
                  firstName: polishedFirst,
                  lastName: polishedLast,
                  email: normalizedEmail,
                }),
              }
            ).then(async (res) => {
              const json = await res.json().catch(() => ({}));
              if (!res.ok) {
                throw new Error(
                  safeString(json?.error || json?.message) ||
                    "Could not update settings."
                );
              }
              return json;
            });

      const returnedUser = data?.user || {};
      const nextUser = {
        ...(auth?.user || {}),
        ...returnedUser,
        firstName: polishedFirst,
        lastName: polishedLast,
        name:
          safeString(returnedUser?.name).trim() ||
          [polishedFirst, polishedLast].filter(Boolean).join(" "),
        fullName:
          safeString(returnedUser?.fullName).trim() ||
          safeString(returnedUser?.name).trim() ||
          [polishedFirst, polishedLast].filter(Boolean).join(" "),
        displayName:
          safeString(returnedUser?.displayName).trim() ||
          safeString(returnedUser?.name).trim() ||
          [polishedFirst, polishedLast].filter(Boolean).join(" "),
        email: safeString(returnedUser?.email).trim() || normalizedEmail,
      };

      setFirstName(polishedFirst);
      setLastName(polishedLast);
      setEmail(nextUser.email);

      if (
        typeof auth?.refreshUserFromApiResponse === "function" &&
        data &&
        typeof data === "object"
      ) {
        auth.refreshUserFromApiResponse(data);
      } else if (typeof auth?.setSession === "function") {
        auth.setSession(
          data?.token || auth.token,
          nextUser,
          data?.stores || auth.stores || [],
          data?.activeStoreId || auth.activeStoreId || ""
        );
      }

      setSaveMessage("Settings updated successfully.");
    } catch (err) {
      setSaveError(
        safeString(err?.message).trim() || "Could not update settings."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ft-page" style={{ padding: 12, gap: 12 }}>
      <div className="ft-pageHeader" style={{ marginBottom: 2 }}>
        <div>
          <div className="ft-h1" style={{ fontSize: "1.4rem", lineHeight: 1.08 }}>
            Settings
          </div>
          <div className="ft-subtle" style={{ marginTop: 4, fontSize: 11.5 }}>
            Update your profile information and account preferences.
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.2fr) minmax(260px, 0.8fr)",
          gap: 12,
          alignItems: "start",
        }}
      >
        <div className="ft-card" style={{ padding: 14 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(15,23,42,0.72)",
              marginBottom: 10,
            }}
          >
            Profile
          </div>

          <form onSubmit={handleSave} style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <label
                  className="ft-caption"
                  style={{ marginTop: 0, fontWeight: 800, fontSize: 11.4 }}
                >
                  First Name
                </label>
                <input
                  className="ft-input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={() => setFirstName((v) => polishName(v))}
                  placeholder="First name"
                  style={{ minWidth: 0, width: "100%" }}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  className="ft-caption"
                  style={{ marginTop: 0, fontWeight: 800, fontSize: 11.4 }}
                >
                  Last Name
                </label>
                <input
                  className="ft-input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={() => setLastName((v) => polishName(v))}
                  placeholder="Last name"
                  style={{ minWidth: 0, width: "100%" }}
                />
              </div>
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label
                className="ft-caption"
                style={{ marginTop: 0, fontWeight: 800, fontSize: 11.4 }}
              >
                Email
              </label>
              <input
                className="ft-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                style={{ minWidth: 0, width: "100%" }}
              />
            </div>

            {saveError ? (
              <div
                className="ft-card"
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(180,35,24,0.12)",
                  background: "rgba(254,243,242,0.9)",
                  color: "#b42318",
                  fontSize: 11.5,
                  fontWeight: 700,
                }}
              >
                {saveError}
              </div>
            ) : null}

            {saveMessage ? (
              <div
                className="ft-card"
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(6,118,71,0.12)",
                  background: "rgba(236,253,243,0.9)",
                  color: "#067647",
                  fontSize: 11.5,
                  fontWeight: 700,
                }}
              >
                {saveMessage}
              </div>
            ) : null}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="submit"
                className="ft-btn ft-btn--primary"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>

        <div className="ft-card" style={{ padding: 14 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(15,23,42,0.72)",
              marginBottom: 10,
            }}
          >
            Account
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <InfoRow label="Name" value={polishedDisplayName || "—"} isPrimary />
            <InfoRow label="Email" value={safeString(email).trim() || "—"} />
            <InfoRow label="Role" value={roleText} />
            <InfoRow label="Store" value={storeName} />
          </div>

          <div
            className="ft-caption"
            style={{
              marginTop: 12,
              fontSize: 11.2,
              lineHeight: 1.5,
            }}
          >
            Names are automatically polished before saving so reporting, filters,
            and exports remain clean and professional.
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, isPrimary = false }) {
  return (
    <div
      style={{
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: 12,
        padding: "10px 12px",
        background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 900,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "rgba(15,23,42,0.54)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: isPrimary ? 13.5 : 12.4,
          fontWeight: isPrimary ? 800 : 700,
          color: isPrimary ? "rgba(15,23,42,0.9)" : "rgba(15,23,42,0.84)",
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
    </div>
  );
}